import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import React from 'react';

import { Common } from 'ws-scripts/modules/common';
import { setModalSettings } from '../actions/modalSettings';
import { setRenderSettings } from '../actions/renderSettings';
import { setMyCarsEnabled, setMyCarsSavedVehicles } from '../actions/myCars';
import LoadingSpinner from '../components/LoadingSpinner';
import Slider from '../components/Slider';
import PhotoSwipeGalleryButton from '../components/photoswipe/PhotoSwipeGalleryButton';
import SingleImage from '../components/SingleImage';
import Cta from '../components/Cta';
import MyCarsButton from '../components/MyCarsButton';
import { getVehiclesForProfile } from '../helpers/mycars';
import viewportSize from '../helpers/viewportSize';
import { debounce } from '../helpers/utility';

import './Widget.scss';

class WidgetContainer extends React.Component {
	constructor(props) {
		super(props);
		this.onResize = this.onResize.bind(this);
		this.trackGa = this.trackGa.bind(this);
	}

	componentDidMount() {
		const {
			widgetInfo: { deviceType }
		} = this.props;

		this.setRenderSettings();
		this.props.updateModalSettings({ modalIsOpen: false });

		const enableMyCars = this.props.updateMyCarsEnabled.bind(this, true);
		const updateVehicles = this.props.updateMyCarsSavedVehicles.bind(this);

		/* eslint ignore no-undef:0 */
		$.subscribe('userProfile-loaded', () => {
			// profile is guaranteed to be there after profile loads
			enableMyCars();
			getVehiclesForProfile($, DDC.userProfile.profileId, updateVehicles);
		});
		/* eslint ignore no-undef:0 */

		$(document).on('ajaxSuccess', (event, data, settings) => {
			const { url } = settings;
			if (
				url.indexOf('/apis/mycars/') > -1 &&
				url.indexOf('fromMedia=true') === -1
			) {
				// userProfileId cookie is used if not a real profile ie. anonymous saving
				const profileId =
					DDC.userProfile.profileId || $.cookie('userProfileId');
				if (profileId) {
					enableMyCars();
					getVehiclesForProfile($, profileId, updateVehicles);
				}
			}
		});

		if (!this.debouncedResize) {
			/* eslint no-undef: 0 */
			this.debouncedResize = debounce(
				this.onResize,
				deviceType === 'MOBILE' ? 250 : 100
			);
		}
		window.addEventListener('resize', this.debouncedResize, false);
	}

	componentWillUnmount() {
		window.removeEventListener('resize', this.debouncedResize, false);
	}

	onResize() {
		this.setRenderSettings();
	}

	setRenderSettings() {
		const {
			prefs,
			renderSettings,
			widgetInfo: { deviceType },
			media
		} = this.props;
		const { viewportWidth, viewportHeight } = viewportSize();
		const innerWidth = viewportWidth;
		const innerHeight = viewportHeight;

		// Widget preferences
		const threeUpRatio = parseFloat(prefs.threeUpRatio) || 1;
		const responsiveDesktopImage = prefs.responsiveDesktopImage !== 'false';

		const isMobile = deviceType === 'MOBILE';
		const responsiveImage = isMobile || responsiveDesktopImage;
		const aspectRatio = innerWidth / innerHeight;
		const numSlides = media.images.length;
		const threeUpDisplay = aspectRatio > threeUpRatio && numSlides > 1; // If page is taller than wide show 1
		const imageMode = !responsiveImage || threeUpDisplay || isMobile; // Always use <Image> elements on mobile

		// Get previous state
		let { currentIndex = 0 } = renderSettings;

		// If we've got no overflow on a 3-up display
		if (threeUpDisplay && numSlides <= 3) {
			// Reset slides to beginning to show all in 3-up
			currentIndex = 0;

			// Don't allow the gallery to change the slide index
			this.props.updateModalSettings({ persistIndex: false });
		} else {
			// Allow the current gallery slide to be shown first when returning to vehicle page
			this.props.updateModalSettings({ persistIndex: true });
		}

		this.props.updateRenderSettings({
			innerWidth,
			innerHeight,
			threeUpDisplay,
			imageMode,
			responsiveImage,
			currentIndex
		});

		if (isMobile && this.sliderCmp) {
			// make sure mobile properly resizes its slider container on dimension change
			this.sliderCmp.refs.slider.setDimensions();
		}
	}

	trackGa(trackingObj) {
		const DDC = window.DDC || {};
		const defaultTrackingObj = {
			event: 'widgetUserEvent',
			widgetName: 'ws-vehicle-media',
			widgetId: this.props.widgetInfo.windowId,
			widgetState: `default (${
				this.props.renderSettings.threeUpDisplay
					? 'tile view'
					: 'full view'
			})`,
			element: '',
			action: 'clicked',
			result: '',
			elementCta: null
		};

		if (DDC.pushToGtmDataLayer) {
			DDC.pushToGtmDataLayer({ ...defaultTrackingObj, ...trackingObj });
		}
	}

	render() {
		const {
			media: { images, videos, videoOrder },
			widgetInfo,
			widgetInfo: {
				deviceType,
				pathParameters,
				accountId: siteId,
				vehicle
			},
			prefs: {
				galleryBackgroundColor,
				galleryIconColor,
				ctaHref1,
				ctaLabel1,
				ctaIsVehicleLink1,
				ctaIsDialogLink1,
				ctaDialogTitle1,
				offsetSharedVehicleImageByOne,
				galleryToolbarPlacement
			},
			updateRenderSettings,
			updateModalSettings,
			renderSettings: {
				innerHeight,
				innerWidth,
				imageMode,
				responsiveImage,
				threeUpDisplay,
				currentIndex
			},
			modalSettings
		} = this.props;

		return (
			<Common
				render={({ sitemap }) => {
					// If renderSettings and innerWidth are not set,
					// we can assume that window is not yet in context
					// indicating the widget has not fully initialized

					if (!innerWidth) {
						return <LoadingSpinner />;
					}

					// If a vehicle's accountId does not match the widget's siteId,
					// we can assume this is a shared vehicle, and thus should not
					// include the first image as props, bc it often features
					// an overlay featuring the wrong dealership
					let imagesToDisplay = [...images];
					let hasImages = true;
					const firstImageIndex = images.findIndex(image => {
						return image.id === 0;
					});

					if (
						siteId !== vehicle.accountId &&
						offsetSharedVehicleImageByOne === 'true'
					) {
						if (imagesToDisplay.length === 1) {
							// TODO - set first image source to no images image
							hasImages = false;
							imagesToDisplay[0].src = `https://${
								window.location.host
							}/v9/media/images/widget-images/v1/no-photo-lrg.png`;
							imagesToDisplay[0].thumbnail =
								imagesToDisplay[0].src;
						} else {
							imagesToDisplay.splice(firstImageIndex, 1);
							// if the first gets spliced out, ids will start at 1, so this fixes that
							if (imagesToDisplay[firstImageIndex].id === 1) {
								imagesToDisplay = imagesToDisplay.map(img => {
									const newImg = {
										id: img.id - 1, // shift every id back 1
										src: img.src,
										thumbnail: img.thumbnail
									};
									return newImg;
								});
							}
						}
					}

					const galleryProps = {
						images: imagesToDisplay,
						widgetInfo,
						modalSettings,
						videos,
						videoOrder,

						// renderSettings,
						currentIndex,
						innerHeight,
						innerWidth,
						imageMode,
						responsiveImage,
						threeUpDisplay,

						updateRenderSettings,
						updateModalSettings,

						// Prefs for PhotoSwipe
						galleryBackgroundColor,
						galleryIconColor,
						onCurrentIndexChange: (newIndex, cb) => {
							if (
								newIndex !==
								this.props.renderSettings.currentIndex
							) {
								updateRenderSettings({
									currentIndex: newIndex
								});
							}
							if (cb) {
								cb(newIndex);
							}
						},
						onPhotoClick: hasImages
							? (
									clickedIndex = this.props.renderSettings
										.currentIndex
							  ) => {
									this.trackGa({
										element: 'Photo Carousel',
										result: 'Opens gallery modal'
									});
									updateModalSettings({
										modalIsOpen: true,
										videoIsOpen: false,
										spin360IsOpen: false,
										currentIndex: clickedIndex
									});
									updateRenderSettings({
										currentIndex: clickedIndex
									});
							  }
							: () => {},
						trackGa: this.trackGa
					};

					const slider = (
						<Slider
							ref={sc => {
								this.sliderCmp = sc;
							}}
							{...galleryProps}
						/>
					);

					const photoSwipeGalleryButton = (
						<PhotoSwipeGalleryButton {...galleryProps} />
					);

					const mycarsButton = this.props.myCars.enabled ? (
						<MyCarsButton
							uuid={pathParameters.vehicleId}
							transparent={deviceType === 'MOBILE'}
							trackGa={this.trackGa}
							myCars={this.props.myCars}
							key="MyCarsButtonComponent"
						/>
					) : null;

					const iconSize = '24';
					const ctaIconSvg1 = (
						<svg
							version="1.1"
							xmlns="http://www.w3.org/2000/svg"
							width={iconSize}
							height={iconSize}
							viewBox="0 0 24 24"
							className="ddc-icon-vehicle-media-cta"
						>
							<path d="M14.5,9.8c-6,0-11.1,3.9-12.5,9.6l0,0c3.3-3.3,8-5,12.5-5l0,0v5L22,12l-7.5-7.5V9.8z" />
						</svg>
					);

					const params = new Map();
					if (ctaIsVehicleLink1 === 'true') {
						params.set('itemId', pathParameters.vehicleId);
					}

					const cta1 = (
						<Cta
							href={sitemap.get(ctaHref1)}
							icon={ctaIconSvg1}
							label={ctaLabel1}
							params={params}
							dialog={ctaIsDialogLink1 === 'true'}
							dialogTitle={ctaDialogTitle1}
							transparent={deviceType === 'MOBILE'}
							trackGa={this.trackGa}
							key="CTA1"
						/>
					);

					const ctaList = [cta1, mycarsButton];

					const ctaContainerClasses = [
						'cta-container',
						'd-flex',
						'd-print-none',
						'position-absolute'
					];
					if (deviceType !== 'MOBILE') {
						ctaContainerClasses.push('my-4');
					}

					const ctas = ctaList.join('').length ? (
						<div className={ctaContainerClasses.join(' ')}>
							{ctaList}
						</div>
					) : null;

					// If the image has been removed as a result of offsetSharedVehicleImageByOne
					// make sure to grab the correct first image, and grab by id in case of
					// random ordering, though we haven't observed that
					const firstImage = imagesToDisplay.find(image => {
						return image.id === 0;
					});

					let firstImageSrc = '';

					if (firstImage) {
						firstImageSrc = firstImage.src;
					}

					const singleImage =
						deviceType === 'DESKTOP' ? (
							<SingleImage
								image={firstImageSrc}
								uuid={pathParameters.vehicleId}
								onClick={() => {
									this.trackGa({
										element: 'Photo',
										result: 'Opens gallery modal',
										widgetState: 'default (thumbnail)'
									});
									updateModalSettings({
										modalIsOpen: true,
										videoIsOpen: false,
										spin360IsOpen: false,
										currentIndex: 0
									});
								}}
								trackGa={this.trackGa}
								myCars={this.props.myCars}
							/>
						) : null;

					// The data-location attribute is used by the Third Party API
					// as a consistent named location for third parties to inject
					// content of a particular type. More information is available at:
					// https://ghe.coxautoinc.com/DDC-WebPlatform/ddc-js-api
					let mediaContent = (
						<div
							className="media-content"
							data-location="vehicle-media-container"
						>
							<div className="media-gallery-toolbar mobile-toolbar w-100 text-center py-1 d-print-none">
								{photoSwipeGalleryButton}
							</div>
							{ctas}
							{slider}
							{singleImage}
						</div>
					);

					if (widgetInfo.deviceType === 'DESKTOP') {
						mediaContent = (
							<div
								className="media-content"
								data-location="vehicle-media-container"
							>
								{ctas}
								{slider}
								{singleImage}
								<div
									className={`media-gallery-toolbar border m-4 ${
										galleryToolbarPlacement === 'top-left'
											? 'absolute-top'
											: 'absolute-bottom'
									} absolute-left d-print-none`}
								>
									{photoSwipeGalleryButton}
								</div>
							</div>
						);
					}

					return mediaContent;
				}}
			/>
		);
	}
}

WidgetContainer.propTypes = {
	prefs: PropTypes.shape({
		threeUpRatio: PropTypes.string,
		responsiveDesktopImage: PropTypes.string,
		galleryBackgroundColor: PropTypes.string,
		galleryIconColor: PropTypes.string,
		ctaHref1: PropTypes.string,
		ctaLabel1: PropTypes.string,
		ctaIsVehicleLink1: PropTypes.string,
		ctaIsDialogLink1: PropTypes.string,
		ctaDialogTitle1: PropTypes.string,
		offsetSharedVehicleImageByOne: PropTypes.string,
		galleryToolbarPlacement: PropTypes.string
	}),
	media: PropTypes.shape({
		images: PropTypes.arrayOf(
			PropTypes.shape({
				id: PropTypes.number,
				src: PropTypes.string,
				thumbnail: PropTypes.string
			})
		).isRequired,
		videos: PropTypes.shape({}),
		videoOrder: PropTypes.arrayOf(
			PropTypes.oneOfType([PropTypes.string, PropTypes.number])
		)
	}).isRequired,
	widgetInfo: PropTypes.shape({
		windowId: PropTypes.string.isRequired,
		deviceType: PropTypes.string,
		vehicle: PropTypes.shape({
			accountId: PropTypes.string
		}).isRequired,
		accountId: PropTypes.string
	}).isRequired,
	renderSettings: PropTypes.shape({
		innerHeight: PropTypes.number,
		innerWidth: PropTypes.number,
		imageMode: PropTypes.bool,
		responsiveImage: PropTypes.bool,
		threeUpDisplay: PropTypes.bool,
		currentIndex: PropTypes.number
	}),
	modalSettings: PropTypes.shape({
		modalIsOpen: PropTypes.bool
	}),
	updateRenderSettings: PropTypes.func.isRequired,
	updateModalSettings: PropTypes.func.isRequired,
	myCars: PropTypes.shape({
		enabled: PropTypes.bool.isRequired,
		savedVehicles: PropTypes.arrayOf(
			PropTypes.shape({
				itemUUID: PropTypes.string
			})
		)
	}),
	updateMyCarsEnabled: PropTypes.func.isRequired,
	updateMyCarsSavedVehicles: PropTypes.func.isRequired
};

WidgetContainer.defaultProps = {
	prefs: {
		threeUpRatio: '1',
		responsiveDesktopImage: 'true',
		offsetSharedVehicleImageByOne: 'false',
		galleryToolbarPlacement: 'bottom-left'
	},
	renderSettings: {
		innerHeight: null,
		innerWidth: null,
		imageMode: false,
		responsiveImage: true,
		threeUpDisplay: false,
		currentIndex: 0
	},
	modalSettings: {
		modalIsOpen: false
	},
	myCars: {
		enabled: false,
		savedVehicles: null
	}
};

const mapStateToProps = state => {
	return {
		prefs: state.prefs,
		media: state.media,
		widgetInfo: state.widgetInfo,
		renderSettings: state.renderSettings,
		modalSettings: state.modalSettings,
		myCars: state.myCars
	};
};

const mapDispatchToProps = dispatch => ({
	updateRenderSettings: props => {
		dispatch(setRenderSettings(props));
	},
	updateModalSettings: props => {
		dispatch(setModalSettings(props));
	},
	updateMyCarsSavedVehicles: props => {
		dispatch(setMyCarsSavedVehicles(props));
	},
	updateMyCarsEnabled: enabled => {
		dispatch(setMyCarsEnabled(enabled));
	}
});

export default connect(
	mapStateToProps,
	mapDispatchToProps
)(WidgetContainer);



// WEBPACK FOOTER //
// ./src/containers/Widget.jsx